// page-meeting-tabs.ts — Barre d'onglets de la fiche réunion et bascule
// d'onglet (persistance dans le hash, garde « notes non enregistrées »).
//
// Extrait de `renderMeetingPage` le 2026-09-08 (point 16). La page garde le
// contenu de chaque onglet et son câblage ; elle les fournit au switcher.
//
// Globaux ambiants : esc, t, confirmStackModal, history (core-globals.d.ts).

import { TAB_ORDER, type MeetingPageState, type TabKey } from '../domain/meeting-form.js';
import { stepBadge, TAB_TO_STEP } from './page-meeting-steps.js';
// Même style d'onglet que les autres écrans à onglets (matrice, revues).
import { tabStyle } from './page-matrix-helpers.js';

export const tabLabel = (k: TabKey): string => t('pageMeeting.tab.' + k);

// Picto « compteur de vitesse » (bleu) pour l'onglet Indicateurs, hors du
// workflow numéroté 1→6. Inline : le sprite icons.svg n'a pas de jauge.
const INDICATOR_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16" aria-hidden="true" style="vertical-align:middle;margin-right:6px;flex:none"><path d="m12 14 4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/></svg>';

/** Barre d'onglets ; vide sans onglet visible. */
export function tabsBarHtml(visibleTabs: ReadonlyArray<TabKey>, activeTab: TabKey): string {
  if (!visibleTabs.length) return '';
  const btnsHtml = visibleTabs
    .map((k) => {
      const n = TAB_TO_STEP[k];
      const badgeHtml = n
        ? stepBadge(n, 'margin-right:6px;vertical-align:middle')
        : k === 'indicators'
          ? INDICATOR_ICON
          : '';
      return `<button type="button" class="mp-tab" data-tab="${k}" style="${tabStyle(activeTab === k)}">${badgeHtml}${esc(tabLabel(k))}</button>`;
    })
    .join('');
  return `<div id="mp-tabs" style="display:flex;gap:4px;border-bottom:1px solid var(--border);padding:0 2px;align-items:flex-end;margin-bottom:14px;flex-wrap:wrap">
      ${btnsHtml}
    </div>`;
}

/** Clic sur un onglet de la barre : demande la bascule (garde incluse). */
export function wireTabsBar(onRequest: (k: TabKey) => void): void {
  document.querySelectorAll<HTMLButtonElement>('.mp-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tabKey = btn.dataset['tab'] as TabKey | undefined;
      if (!tabKey || !TAB_ORDER.includes(tabKey)) return;
      onRequest(tabKey);
    });
  });
}

export interface TabSwitcherContext {
  state: Pick<MeetingPageState, 'activeTab'>;
  /** Réunion enregistrée : l'onglet est persisté dans le hash. */
  meetingId: number | null;
  /** Onglet Notes : saisie non enregistrée ? */
  isDirty: () => boolean;
  markClean: () => void;
  /** Avant de quitter un onglet (arrêt du polling des brouillons). */
  onLeave: () => void;
  renderContent: () => string;
  wireContent: () => void;
}

export interface TabSwitcher {
  /** Bascule immédiate, sans garde. */
  switchTab: (next: TabKey) => void;
  /** Bascule demandée par l'UI : garde « notes non enregistrées » appliquée
   * une fois ; `onDone` s'exécute après la bascule effective. */
  requestSwitchTab: (next: TabKey, onDone?: () => void) => void;
}

export function createTabSwitcher(ctx: TabSwitcherContext): TabSwitcher {
  function switchTab(next: TabKey): void {
    ctx.state.activeTab = next;
    ctx.onLeave();
    // Persistance dans le hash sans déclencher le router (pas de hashchange).
    if (ctx.meetingId != null) {
      history.replaceState({}, '', `#meeting/${ctx.meetingId}?tab=${next}`);
    }
    document.querySelectorAll<HTMLButtonElement>('.mp-tab').forEach((b) => {
      b.setAttribute('style', tabStyle(b.dataset['tab'] === next));
    });
    const host = document.getElementById('mp-tab-content');
    if (!host) return;
    host.innerHTML = ctx.renderContent();
    ctx.wireContent();
  }

  function requestSwitchTab(next: TabKey, onDone?: () => void): void {
    if (next === ctx.state.activeTab) {
      onDone?.();
      return;
    }
    if (ctx.state.activeTab === 'notes' && ctx.isDirty()) {
      void (async () => {
        const ok = await confirmStackModal({
          message: t('common.unsaved_leave_confirm'),
          danger: true,
          confirmLabel: t('common.unsaved_leave_btn'),
        });
        if (!ok) return;
        ctx.markClean();
        switchTab(next);
        onDone?.();
      })();
      return;
    }
    switchTab(next);
    onDone?.();
  }

  return { switchTab, requestSwitchTab };
}

/**
 * Verrous de palier posés après le rendu (cadenas + bandeau d'upsell) :
 *
 * - mode Solo : l'onglet Participants, les deux champs RACI et le CTA de
 *   proposition — l'utilisateur est seul, il n'a pas de collaborateurs ;
 * - onglet Indicateurs : réservé aux paliers Pilotage et Intelligence.
 *
 * `window.markLocked` est optionnel (chargé avec le composant d'upsell) :
 * sans lui, rien n'est verrouillé visuellement, le backend refuse de toute
 * façon.
 */
export function applyMeetingLocks(caps: { lockSolo: boolean; indicatorsAllowed: boolean }): void {
  const markLocked = window.markLocked;
  if (!markLocked) return;
  const lock = (el: HTMLElement | null, kind: 'solo' | 'premium'): void => {
    if (el) markLocked(el, kind);
  };
  if (caps.lockSolo) {
    lock(document.querySelector<HTMLElement>('.mp-tab[data-tab="participants"]'), 'solo');
    for (const field of ['mp-animators', 'mp-participants']) {
      lock(document.getElementById('raci-' + field) || document.getElementById(field), 'solo');
    }
    lock(document.getElementById('mp-propose-btn'), 'solo');
  }
  if (!caps.indicatorsAllowed) {
    lock(document.querySelector<HTMLElement>('.mp-tab[data-tab="indicators"]'), 'premium');
  }
}
